import type { SchemaData, SerializedHistory, Step } from '@lemonade-hq/chat-engine';
import { useWizardNavigationContext } from '@lemonade-hq/chat-wizard-client-base';
import { useMonitoringContext } from '@lemonade-hq/nuiza';
import type { Milliseconds, Nullish } from '@lemonade-hq/ts-helpers';
import { camelCaseToReadable, devWarn } from '@lemonade-hq/ts-helpers';
import { useUniclientTrackingContext } from '@lemonade-hq/uniclient-app';
import { useTheme } from '@lemonade-hq/uniclient-theme';
import type { Theme } from '@lemonade-hq/uniclient-theme';
import type { AxiosError } from 'axios';
import axios, { HttpStatusCode, isAxiosError } from 'axios';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatEngineSession } from './chat_engine.client';
import { createLocalSessionFromStep, wrapStepInRenderNode } from './createLocalSessionFromStep';
import { useChatServerPersistence } from './useChatServerPersistence';
import { useConversationRouterPersistence } from './useConversationRouterPersistence';
import { usePagePersistence } from './usePagePersistence';
import { usePersistenceHelpers } from './usePersistenceHelpers';
import type {
  WizardExtendedStepWithPersistence,
  WizardPersistenceContextSubmissionOptions,
  WizardPersistenceLoadSessionProps,
} from './wizard_persistence.context';
import { WizardPersistenceContext } from './wizard_persistence.context';

// TYPES

export interface WizardChatEnginePersistenceProps<
  TPresentation = undefined,
  TDataExtension = unknown,
  TStep extends WizardExtendedStepWithPersistence<
    Step<TPresentation, TDataExtension>
  > = WizardExtendedStepWithPersistence<Step<TPresentation, TDataExtension>>,
> extends WizardPersistenceLoadSessionProps {
  readonly errorMap?: (error: AxiosError, errorType: 'answer' | 'init') => TStep | undefined;
  readonly serverTimeoutOverride?: Milliseconds;
  readonly redirectFn?: (url: string) => Promise<void> | void;
  readonly resourceType?: 'chat' | 'conversationRouter' | 'page';
  readonly theme?: Theme;
}

// HELPERS

function isCustomChatError(error: AxiosError): boolean {
  return (
    error.response != null &&
    'status' in error.response &&
    (error.response.status as HttpStatusCode) === HttpStatusCode.PreconditionFailed
  );
}

// MAIN

export const WizardChatEnginePersistenceProvider = React.memo(
  <
    TPresentation = undefined,
    TDataExtension = unknown,
    TStep extends WizardExtendedStepWithPersistence<
      Step<TPresentation, TDataExtension>
    > = WizardExtendedStepWithPersistence<Step<TPresentation, TDataExtension>>,
  >({
    children,
    scriptName: scriptNameProp,
    serverTimeoutOverride: serverTimeoutOverrideProp,
    errorMap,
    redirectFn,
    resourceType = 'chat',
    theme: themeProp,
    ...props
  }: PropsWithChildren<WizardChatEnginePersistenceProps<TPresentation, TDataExtension, TStep>>): JSX.Element => {
    const { trackEvent } = useUniclientTrackingContext();
    const { reportException } = useMonitoringContext();
    const { currentStepIndex } = useWizardNavigationContext();
    const { setTheme, getThemeImageUrl, theme } = useTheme();
    const abortController = useRef<AbortController | null>(null);
    const [session, setSession] = useState<ChatEngineSession<TPresentation> | null>(null);
    const hasInitializationStartedRef = useRef<string | null>(null);
    const [
      { isLoadingInitialServerData, isSubmittingToServer, serverError, isSubmittingLLMRequest, isErrorFromLLMRequest },
      setUiState,
    ] = useState<{
      isLoadingInitialServerData: boolean;
      isSubmittingToServer: boolean;
      serverError?: Nullish | boolean | string;
      isSubmittingLLMRequest: boolean;
      isErrorFromLLMRequest: boolean;
    }>({
      isLoadingInitialServerData: true,
      isSubmittingToServer: false,
      serverError: null,
      isSubmittingLLMRequest: false,
      isErrorFromLLMRequest: false,
    });

    const { history, sessionId, auth } = session ?? {};

    const { initSession: initChatSession, submitAnswersToServer: submitAnswersToChatServer } =
      useChatServerPersistence<TPresentation>({
        abortController,
        setSession,
        setUiState,
        sessionId,
        currentStepId: history?.[currentStepIndex]?.id,
        serverBaseUrl: props.serverBaseUrl,
        scriptName: scriptNameProp,
        redirectFn,
        serverTimeoutOverride: serverTimeoutOverrideProp,
      });

    const {
      initSession: initConversationRouterSession,
      submitAnswersToServer: submitAnswersToConversationRouterServer,
    } = useConversationRouterPersistence<TPresentation>({
      abortController,
      setSession,
      setUiState,
      sessionId,
      currentStepId: history?.[currentStepIndex]?.id,
      currentStepIndex,
      serverBaseUrl: props.serverBaseUrl,
      scriptName: scriptNameProp,
      redirectFn,
      serverTimeoutOverride: serverTimeoutOverrideProp,
      session,
    });

    const { initSession: initPageSession, submitAnswersToServer: submitAnswersToPageServer } =
      usePagePersistence<TPresentation>({
        abortController,
        setSession,
        setUiState,
        sessionId,
        currentStepId: history?.[currentStepIndex]?.id,
        serverBaseUrl: props.serverBaseUrl,
        scriptName: scriptNameProp,
        redirectFn,
        serverTimeoutOverride: serverTimeoutOverrideProp,
      });

    // SETUP - INIT
    const initSession = useCallback(
      ({ clearPreviousSession, scriptName, ...loadSessionProps }: WizardPersistenceLoadSessionProps) => {
        if (clearPreviousSession) {
          setSession(null);
        }

        let initFn: (props: WizardPersistenceLoadSessionProps) => Promise<void>;

        switch (resourceType) {
          case 'chat':
            initFn = initChatSession;
            break;
          case 'conversationRouter':
            initFn = initConversationRouterSession;
            break;
          case 'page':
            initFn = initPageSession;
            break;
          default:
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            throw new Error(`Unsupported resource type: ${resourceType}`);
        }

        initFn({
          scriptName,
          ...loadSessionProps,
        }).catch((error: unknown) => {
          const axiosError = error as AxiosError;
          const errorType = camelCaseToReadable(resourceType);
          reportException(new Error(`Init ${errorType} failed for ${scriptName}. Error: ${axiosError.message}`), {
            tags: { scriptName, clientType: errorType },
          });

          const errorStep = errorMap?.(axiosError, 'init');
          if (errorStep != null) {
            const localSession = createLocalSessionFromStep<TPresentation, TDataExtension>(scriptName, errorStep);
            setSession(localSession);
            setUiState(val => {
              return { ...val, isLoadingInitialServerData: false };
            });
            return;
          }

          setUiState(val => {
            return { ...val, serverError: true };
          });
        });
      },
      [errorMap, reportException, resourceType, initChatSession, initConversationRouterSession, initPageSession],
    );

    useEffect(() => {
      // make sure this effect only runs once, we should not rely on the dependencies array by itself
      /* istanbul ignore next -- @preserve - missing test */
      if (hasInitializationStartedRef.current === scriptNameProp) return;

      hasInitializationStartedRef.current = scriptNameProp;

      initSession({
        scriptName: scriptNameProp,
        serverTimeoutOverride: serverTimeoutOverrideProp,
        ...props,
      });
    }, [scriptNameProp]); // eslint-disable-line react-hooks/exhaustive-deps -- this should happen only on init or scriptName change

    // ACTIONS - SUBMIT ANSWERS

    const onSubmitError = useCallback(
      (
        error: unknown,
      ): { history: SerializedHistory<TPresentation>; success: boolean } | { history: TStep[]; success: boolean } => {
        if (isAxiosError<{ message: string }>(error) && isCustomChatError(error)) {
          setUiState(val => {
            return {
              ...val,
              isSubmittingToServer: false,
              serverError: error.response?.data.message,
            };
          });
          throw error;
        } else if (axios.isCancel(error)) {
          abortController.current = null;
          setUiState(val => {
            return { ...val, isSubmittingToServer: false };
          });
          trackEvent('chat.submission_error', {
            reason: 'timeout',
            resourceType,
          });
          return { history: session?.history ?? [], success: false };
        } else {
          const axiosError = error as AxiosError;
          const errorStep = errorMap?.(axiosError, 'answer');

          if (errorStep != null) {
            const prevHistory = session?.history ?? [];
            const newHistory = [
              ...prevHistory,
              wrapStepInRenderNode<TPresentation, TDataExtension>(scriptNameProp, errorStep),
            ];
            setSession(
              sessionToUpdate => ({ ...sessionToUpdate, history: newHistory }) as ChatEngineSession<TPresentation>,
            );
            setUiState(val => {
              return { ...val, isSubmittingToServer: false };
            });
            return { history: newHistory, success: true };
          }

          const clientType = resourceType === 'conversationRouter' ? 'conversation-router' : 'chat-engine';
          const message = `Submit ${clientType} failed for ${scriptNameProp}. Error: ${axiosError.message}`;
          devWarn(message);
          reportException(new Error(message), { tags: { scriptName: scriptNameProp, clientType } });
          setUiState(val => {
            return { ...val, isSubmittingToServer: false, serverError: true };
          });
          throw error;
        }
      },
      [session, errorMap, scriptNameProp, resourceType, reportException, trackEvent],
    );

    const submitAnswersToServer = useCallback(
      async (answers: SchemaData, options?: WizardPersistenceContextSubmissionOptions) => {
        try {
          let submitFn: (
            answers: SchemaData,
            options?: WizardPersistenceContextSubmissionOptions,
            onSubmitError?: (error: unknown) => void,
          ) => Promise<{
            history: SerializedHistory<TPresentation>;
            success: boolean;
          }>;

          switch (resourceType) {
            case 'chat':
              submitFn = submitAnswersToChatServer;
              break;
            case 'conversationRouter':
              submitFn = submitAnswersToConversationRouterServer;
              break;
            case 'page':
              submitFn = submitAnswersToPageServer;
              break;
            default:
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              throw new Error(`Unsupported resource type: ${resourceType}`);
          }

          setUiState(val => {
            return {
              ...val,
              isSubmittingToServer: true,
              serverError: null,
              isSubmittingLLMRequest: false,
              isErrorFromLLMRequest: false,
            };
          });

          return await submitFn(answers, options, onSubmitError);
        } catch (error: unknown) {
          return onSubmitError(error);
        }
      },
      [
        resourceType,
        submitAnswersToChatServer,
        submitAnswersToConversationRouterServer,
        submitAnswersToPageServer,
        onSubmitError,
      ],
    );

    // CANCEL ONGOING REQUEST IF NAVIGATION HAPPENS

    useEffect(() => {
      abortController.current?.abort();
      abortController.current = null;
    }, [currentStepIndex]);

    // SERIALIZE
    const { answersSchema, persistedAnswersByStep, steps, truncateObsoleteLocalSteps } = usePersistenceHelpers({
      history,
      currentStepIndex,
      session,
      setSession,
    });

    const themedAssets = useMemo(() => {
      return session == null || (theme.brand === 'pinkish' && theme.scheme === 'light')
        ? []
        : session.assets.map(asset => {
            return getThemeImageUrl(asset);
          });
    }, [session, getThemeImageUrl, theme.brand, theme.scheme]);

    useEffect(() => {
      if (themeProp != null) {
        setTheme(themeProp);
      }
    }, [themeProp, setTheme]);

    const value = useMemo(
      () => ({
        submitAnswers: submitAnswersToServer,
        steps,
        truncateObsoleteLocalSteps,
        persistedAnswersByStep,
        answersSchema,
        isInitializing: isLoadingInitialServerData || hasInitializationStartedRef.current !== scriptNameProp,
        isSubmitting: isSubmittingToServer,
        assets: themedAssets,
        error: serverError,
        isSubmittingLLMRequest,
        isErrorFromLLMRequest,
        sessionStatus: session?.sessionStatus,
        sessionId: session?.sessionId,
        auth,
        initSession,
      }),
      [
        answersSchema,
        isSubmittingToServer,
        isLoadingInitialServerData,
        persistedAnswersByStep,
        serverError,
        isSubmittingLLMRequest,
        isErrorFromLLMRequest,
        themedAssets,
        session?.sessionStatus,
        session?.sessionId,
        steps,
        truncateObsoleteLocalSteps,
        submitAnswersToServer,
        auth,
        scriptNameProp,
        initSession,
      ],
    );

    return <WizardPersistenceContext.Provider value={value}>{children}</WizardPersistenceContext.Provider>;
  },
);
